import { forwardRef, useId } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import { FieldFrame, describedBy, fieldControlClass } from '@/components/ui/Input'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: string
  error?: string
  id?: string
  /** Alternativa a passar `<option>` como children. */
  options?: SelectOption[]
  /** Primeira opção, sem valor, usada como instrução. */
  placeholder?: string
  containerClassName?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    required,
    id,
    options,
    placeholder,
    containerClassName,
    className,
    children,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const controlId = id ?? `select-${generatedId}`
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`

  return (
    <FieldFrame
      controlId={controlId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hintId={hintId}
      errorId={errorId}
      className={containerClassName}
    >
      <div className="relative">
        <select
          ref={ref}
          id={controlId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(
            hint && !error && hintId,
            error && errorId,
            ariaDescribedBy,
          )}
          className={cn(
            fieldControlClass(Boolean(error)),
            'h-10 cursor-pointer appearance-none pr-9',
            className,
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options?.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[var(--text-muted)]"
        />
      </div>
    </FieldFrame>
  )
})
